import type { ReactNode } from "react";

import { cn } from "@/utils/cn";

// Figma: https://www.figma.com/design/72p7BRSjceW2TlzRIHHRAB/Evvy-Core-Design-System?node-id=429-2373

export const BADGE_VARIANTS = [
  "callout",
  "callout-compact",
  "callout-mini",
  "list",
  "detail",
] as const;

export type BadgeVariant = (typeof BADGE_VARIANTS)[number];

type BadgeProps = {
  children: ReactNode;
  /** callout draws attention ("New"), list comes in multiples (symptoms), detail is a small status. */
  variant?: BadgeVariant;
  /** Optional color treatment shared across badge variants. */
  tone?: "default" | "lilac";
  /** false renders the frosted-glass style, for use on imagery or tinted backgrounds. */
  solid?: boolean;
  /** Layout only. A repeated look is a variant. */
  className?: string;
};

const BASE =
  "inline-flex w-fit items-center justify-center gap-ds-space-02 whitespace-nowrap text-center text-ds-text-primary";

// Mobile first; md: is the Figma "desktop" size.
// The mobile 8px and 10px corners and 10px callout text have no Figma variable (EVY-436).
const VARIANTS: Record<BadgeVariant, string> = {
  callout: cn(
    "rounded-[8px] px-ds-space-03 py-ds-space-02 md:rounded-ds-corner-base md:p-ds-space-03",
    "text-[10px] font-semibold uppercase leading-[1.2] tracking-wider md:text-ds-header-subheader-desktop"
  ),
  "callout-compact":
    "ds-header-subheader rounded-ds-corner-base px-ds-space-03 py-ds-space-01",
  "callout-mini":
    "ds-header-subheader h-[20px] rounded-[8px] px-ds-space-03 py-0 text-[10px] leading-[12px] tracking-[0.5px] md:text-[10px] md:leading-[12px]",
  list: cn(
    "rounded-[10px] px-ds-space-04 py-ds-space-02 md:rounded-ds-corner-base",
    "ds-body-small-medium"
  ),
  detail: cn(
    "rounded-ds-corner-small p-ds-space-02 md:px-ds-space-03",
    "ds-body-small-medium"
  ),
};

const SOLID_BACKGROUNDS: Record<BadgeVariant, string> = {
  callout: "bg-ds-all-colors-electric",
  "callout-compact": "bg-ds-all-colors-electric",
  "callout-mini": "bg-ds-all-colors-electric",
  list: "bg-ds-background-creme",
  detail: "bg-ds-background-creme",
};

// Figma Shadow/01. The white 50% fill has no variable.
const GLASS =
  "bg-ds-background-primary/50 backdrop-blur-ds-shadow-01 shadow-ds-shadow-01";

// Display component, no ref forwarded. See ui/README.md.
export const Badge = ({
  children,
  variant = "callout",
  tone = "default",
  solid = true,
  className,
}: BadgeProps) => {
  return (
    <span
      className={cn(
        BASE,
        VARIANTS[variant],
        solid
          ? tone === "lilac"
            ? "bg-ds-all-colors-lilac/50"
            : SOLID_BACKGROUNDS[variant]
          : GLASS,
        className
      )}
    >
      {children}
    </span>
  );
};
